import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SearchAutoComplete from "../../components/shared/SearchAutoComplete";
import { Box, HStack, VStack } from "../../components/utils";
import { useEmployees } from "../../queries";
import BCHeader from "../../shared/BCHeader";
import { Employee } from "../../types/Employee";
import ActivityLog from "./ActivityLog";
import Documents from "./Documents";
import EmployeeHeader from "./EmployeeHeader";
import GrantsVSGrade from "./GrantsVSGrade";
import Profile from "./profile";
import { Resignation } from "./Resignation";

function EmployeeDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: _employees, isFetching, isPlaceholderData } = useEmployees();
  const employees: Employee[] = useMemo(
    () => _employees || [],
    [id, isFetching, isPlaceholderData]
  );

  const initialSelectedEmp = useMemo(
    () => employees.find((emp) => emp.id === id),
    [id, employees]
  );

  const [activeTab, setActiveTab] = useState<string>("Grant Details");
  const [selectedEmp, setSelectedEmp] = useState<
    | Employee
    | {
        id: "";
        employeeName: string;
      }
  >();
  const showTabs = (e: React.MouseEvent<HTMLElement>) => {
    setActiveTab((e.target as any).innerHTML);
    e.stopPropagation();
  };

  function getSelectedValue(employee: Employee) {
    if (employee?.id) {
      navigate(`/options/employeeDetails/${employee.id}`);

      if (activeTab === "Resignation") {
        setActiveTab("Grant Details");
      }
    }
  }

  useEffect(() => {
    if (!id || !employees) return;
    if (!initialSelectedEmp) return;

    if (initialSelectedEmp) {
      setSelectedEmp(initialSelectedEmp);
    } else {
      toast(
        `You don't have permission to access this Employee with id: ${id}`,
        {
          type: "error",
          autoClose: 3000,
        }
      );
      navigate("/options/allEmployees/");
    }
  }, [id, initialSelectedEmp]);

  return (
    <VStack className={`gap-4 ${isPlaceholderData ? "loading" : ""} `}>
      <HStack className="items-center justify-between h-8">
        <BCHeader
          className="items-baseline "
          bcTitle="Stakeholders"
          bcSubTitle={selectedEmp?.employeeName ?? ""}
        />
        {selectedEmp ? (
          <Box className="w-48 h-8 bg-white rounded">
            <SearchAutoComplete
              data={employees}
              getSelectedValue={getSelectedValue}
              selectedOption={selectedEmp}
              field={"employeeName"}
              placeholder="Select Employee"
            />
          </Box>
        ) : (
          ""
        )}
      </HStack>
      <EmployeeHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onClick={showTabs}
      />
      {activeTab === "Grant Details" && <GrantsVSGrade />}
      {activeTab === "Profile" && <Profile />}
      {activeTab === "Activity Log" && <ActivityLog />}
      {activeTab === "Documents" && <Documents />}
      {activeTab === "Resignation" && (
        <Resignation setActiveTab={setActiveTab} />
      )}
    </VStack>
  );
}

export default EmployeeDetails;
